/** Cliente del servidor propio (/api). La sesión va en una cookie HttpOnly; aquí nunca se ve. */

export class ApiError extends Error { constructor(public status: number, message: string) { super(message); } }

export interface Me { email: string; displayName: string; role?: 'admin' | 'member'; workbooks: Array<{ kind: 'finance' | 'tricount'; fileId: string; name: string }>; prefs?: Record<string, unknown> }
export interface UserSummary { id: string; email: string; displayName: string; role: 'admin' | 'member'; active: boolean; createdAt: string; lastSignInAt: string; deactivatedAt: string; hasWorkbook: boolean }
export interface BankConnectionView {
  account: string; bank: string; bankName: string; accountLabel: string; connected: boolean; expired: boolean; validUntil: string; daysLeft: number | null;
  lastSyncAt: string; syncsToday: number; maxPerDay: number; choices: Array<{ uid: string; label: string }>;
}
/** Ficha de un banco en el catálogo de Enable Banking (diagnóstico, solo administrador). */
export interface AspspCard {
  name: string; country: string; bic: string; beta: boolean; psuTypes: string[]; maxConsentDays: number | null; requiredPsuHeaders: string[];
  authMethods: Array<{ name: string; title: string; psuType: string; approach: string; hidden: boolean; credentials: Array<{ name: string; title: string; required: boolean; description: string }> }>;
}
export interface AspspInfo { bank: string; label: string; chosen: string | null; total: number; matches: AspspCard[] }
export interface BankStatus { configured: boolean; banks: Array<{ id: string; label: string }>; connections: BankConnectionView[] }
/** Pedidos de Amazon: la dirección privada para reenviar y lo que hay pendiente de recoger. */
export interface MailInbox { address: string | null; forwarder: string; createdAt: string; pending: number; confirm: boolean }
export interface MailItem { id: string; kind: 'amazon' | 'gmail-confirm'; receivedAt: string; size: number }
export interface ClientConfig { googleClientId: string; projectNumber: string; pickerApiKey: string }

/** En la demostración no se habla con el servidor: ni con tu sesión ni con tus datos reales. */
let blocked = '';
export const blockApi = (why: string) => { blocked = why; };

async function call<T>(path: string, init: RequestInit = {}): Promise<T> {
  if (blocked) throw new ApiError(0, blocked);
  const mutating = init.method && init.method !== 'GET';
  const r = await fetch(`/api${path}`, { credentials: 'same-origin', ...init,
    headers: { ...(mutating ? { 'x-pf': '1', 'content-type': 'application/json' } : {}), ...(init.headers ?? {}) } });
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new ApiError(r.status, (j as { error?: string }).error ?? `Error ${r.status}`);
  return j as T;
}

/** Un fichero tal cual (p. ej. un email recibido), sin pasar por JSON. */
async function bytes(path: string): Promise<ArrayBuffer> {
  if (blocked) throw new ApiError(0, blocked);
  const r = await fetch(`/api${path}`, { credentials: 'same-origin' });
  if (!r.ok) throw new ApiError(r.status, ((await r.json().catch(() => ({}))) as { error?: string }).error ?? `Error ${r.status}`);
  return r.arrayBuffer();
}

let token: { accessToken: string; expiresAt: number } | null = null;

export const api = {
  me: () => call<Me>('/me'),
  config: () => call<ClientConfig>('/config'),
  /** Token de Google de corta duración para leer el libro directamente; se reutiliza hasta 2 minutos antes de caducar. */
  async accessToken(): Promise<string> {
    if (token && token.expiresAt - Date.now() > 120000) return token.accessToken;
    let t;
    try { t = await call<{ accessToken: string; expiresAt: string }>('/auth/token'); }
    catch (err) {
      // Google ya no deja usar el permiso (lo retiraste, cambió la contraseña o caducó): que la app lo diga y ofrezca darlo otra vez
      if (err instanceof ApiError && err.status === 401) dispatchEvent(new CustomEvent('pf-reauth', { detail: err.message }));
      throw err;
    }
    token = { accessToken: t.accessToken, expiresAt: Date.parse(t.expiresAt) };
    return t.accessToken;
  },
  setWorkbook: (kind: 'finance' | 'tricount', fileId: string) => call<{ fileId: string; name: string }>(`/workbook/${kind}`, { method: 'PUT', body: JSON.stringify({ fileId }) }),
  setPref: (key: string, value: unknown) => call<{ ok: true }>(`/prefs/${key}`, { method: 'PUT', body: JSON.stringify({ value }) }),
  admin: {
    users: () => call<UserSummary[]>('/admin/users'),
    invite: (email: string, name: string) => call<UserSummary>('/admin/users', { method: 'POST', body: JSON.stringify({ email, name }) }),
    setActive: (id: string, active: boolean) => call<{ ok: true }>(`/admin/users/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify({ active }) }),
  },
  deleteAccount: () => call<{ ok: true }>('/account', { method: 'DELETE' }),
  bank: {
    status: () => call<BankStatus>('/bank/status'),
    connect: (account: string, bank: string) => call<{ url: string }>('/bank/connect', { method: 'POST', body: JSON.stringify({ account, bank }) }),
    select: (account: string, uid: string) => call<{ ok: true }>('/bank/select', { method: 'POST', body: JSON.stringify({ account, uid }) }),
    sync: (account: string, minDate: string) => call<{ from: string; balances: unknown[]; transactions: unknown[]; syncsToday: number }>('/bank/sync', { method: 'POST', body: JSON.stringify({ account, minDate }) }),
    disconnect: (account: string) => call<{ ok: true }>('/bank/disconnect', { method: 'POST', body: JSON.stringify({ account }) }),
    aspsp: (bank: string) => call<AspspInfo>(`/bank/aspsp?bank=${encodeURIComponent(bank)}`),
  },
  mail: {
    inbox: () => call<MailInbox>('/mail/inbox'),
    createInbox: () => call<{ address: string }>('/mail/inbox', { method: 'POST', body: '{}' }),
    deleteInbox: () => call<{ ok: true }>('/mail/inbox', { method: 'DELETE' }),
    list: () => call<MailItem[]>('/mail'),
    raw: (id: string) => bytes(`/mail/${encodeURIComponent(id)}`),
    ack: (ids: string[]) => call<{ ok: true }>('/mail/ack', { method: 'POST', body: JSON.stringify({ ids }) }),
  },
  ingestTokens: {
    list: () => call<Array<{ purpose: 'tickets' | 'tricount'; createdAt: string }>>('/ingest-tokens'),
    create: (purpose: 'tickets' | 'tricount') => call<{ purpose: string; key: string }>('/ingest-tokens', { method: 'POST', body: JSON.stringify({ purpose }) }),
    remove: (purpose: 'tickets' | 'tricount') => call<{ ok: true }>(`/ingest-tokens/${purpose}`, { method: 'DELETE' }),
  },
  logout: () => call<{ ok: true }>('/auth/logout', { method: 'POST', body: '{}' }),
};
